import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Star, Play, Loader2, ExternalLink, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (s) => (s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');
const useList = (url, token, dep) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(url, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [url, token, dep]);
  return rows;
};

export const AttendeesTab = ({ slug, token }) => {
  const [tick, setTick] = useState(0);
  const [sending, setSending] = useState(false);
  const rows = useList(`${API}/api/admin/lectures/${slug}/attendees`, token, tick);
  const sendSummary = async () => {
    const withScore = (rows || []).filter((r) => Object.keys(r.rcount || {}).length);
    const unsent = withScore.filter((r) => Object.values(r.rcount).some((x) => !x.summary_sent_at)).length;
    const resend = unsent === 0 && withScore.length > 0;
    if (!withScore.length) { toast.info('Nessuno studente ha ancora un risultato /r/'); return; }
    if (!window.confirm(resend ? `Tutti hanno già ricevuto il riepilogo. Lo invio di nuovo a ${withScore.length} studenti?` : `Invio il riepilogo /r/ a ${unsent} studenti che non l'hanno ancora ricevuto?`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/lectures/${slug}/rcount-summary?resend=${resend}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Riepilogo inviato a ${data.sent} studenti${data.failed ? ` · ${data.failed} non riusciti` : ''}`);
      setTick((x) => x + 1);
    } catch { toast.error('Invio non riuscito'); } finally { setSending(false); }
  };
  const exportCsv = async () => {
    const r = await axios.get(`${API}/api/admin/lectures/${slug}/attendees.csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `iscritti-${slug}.csv`; a.click();
  };
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-blue-600" />;
  return (
    <div className="space-y-4" data-testid="admin-attendees">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600"><b data-testid="admin-attendees-count">{rows.length}</b> iscritti</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={sendSummary} disabled={sending} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold" data-testid="admin-rcount-summary-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Invia riepilogo /r/</button>
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold" data-testid="admin-attendees-export"><Download className="w-4 h-4" /> Esporta CSV per il CLA</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{['Nome', 'Email', 'Corso di laurea', 'Ateneo', 'Registrato', 'Compito', 'Valutazione', 'Ascolto /r/'].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`admin-attendee-${r.id}`}>
                <td className="px-4 py-3 font-semibold">{r.name}</td><td className="px-4 py-3">{r.email}</td><td className="px-4 py-3">{r.degree_course}</td>
                <td className="px-4 py-3">{r.university}</td><td className="px-4 py-3 text-slate-500">{fmt(r.registered_at)}</td>
                <td className="px-4 py-3">{r.homework ? (r.homework_grade ? `voto ${r.homework_grade}/5` : 'consegnato') : '—'}</td>
                <td className="px-4 py-3">{r.assessment ? 'richiesta' : '—'}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`admin-attendee-rcount-${r.id}`}>{Object.keys(r.rcount || {}).length ? Object.entries(r.rcount).map(([k, x]) => <div key={k}><b>{x.score}</b>/100 · {x.caught}/{x.real} · {k.split('-')[0][0].toUpperCase()}{x.summary_sent_at ? ' ✉' : ''}</div>) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReviewBox = ({ hw, token, onSaved }) => {
  const [grade, setGrade] = useState(hw.review?.grade || 0);
  const [comment, setComment] = useState(hw.review?.comment || '');
  const [audio, setAudio] = useState('');
  const [busy, setBusy] = useState(false);
  const loadAudio = async () => {
    const r = await axios.get(`${API}/api/admin/lecture-homework/${hw.id}/audio`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
    setAudio(URL.createObjectURL(r.data));
  };
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/api/admin/lecture-homework/${hw.id}/review`, { grade, comment }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`Valutazione inviata a ${hw.name}`); onSaved(); }
    catch { toast.error('Salvataggio non riuscito'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid={`admin-homework-${hw.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-bold">{hw.name} <span className="font-normal text-slate-500 text-sm">· {hw.email}</span></p>
        <p className="text-xs text-slate-500">Consegnato {fmt(hw.submitted_at)}</p>
      </div>
      <a href={hw.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-700 underline break-all"><ExternalLink className="w-3.5 h-3.5" /> {hw.video_url}</a>
      <p className="text-sm text-slate-700 whitespace-pre-line bg-slate-50 rounded-lg p-3">{hw.analysis}</p>
      {audio ? <audio controls src={audio} className="w-full" /> : hw.audio_mime && <button onClick={loadAudio} className="flex items-center gap-2 text-sm font-semibold text-blue-700" data-testid={`admin-homework-audio-${hw.id}`}><Play className="w-4 h-4" /> Ascolta la registrazione</button>}
      <div className="flex items-center gap-1" data-testid={`admin-homework-grade-${hw.id}`}>
        {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setGrade(n)} aria-label={`voto ${n}`}><Star className={`w-6 h-6 ${n <= grade ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} /></button>)}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Commento per lo studente" className="w-full rounded-lg border border-slate-300 p-3 text-sm" data-testid={`admin-homework-comment-${hw.id}`} />
      <button onClick={save} disabled={busy || !grade || !comment.trim()} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40" data-testid={`admin-homework-save-${hw.id}`}>{hw.review ? 'Aggiorna e reinvia' : 'Invia valutazione'}</button>
    </div>
  );
};

export const HomeworkTab = ({ slug, token }) => {
  const [n, setN] = useState(0);
  const rows = useList(`${API}/api/admin/lectures/${slug}/homework`, token, n);
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-blue-600" />;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-homework-empty">Nessun compito consegnato.</p>;
  return <div className="space-y-4" data-testid="admin-homework-list">{rows.map((h) => <ReviewBox key={h.id} hw={h} token={token} onSaved={() => setN((x) => x + 1)} />)}</div>;
};

export const AssessmentsTab = ({ slug, token }) => {
  const rows = useList(`${API}/api/admin/lectures/${slug}/assessments`, token);
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-blue-600" />;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="admin-assessments-empty">Nessuna richiesta di valutazione.</p>;
  return (
    <div className="space-y-3" data-testid="admin-assessments">
      {rows.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="font-bold">{a.name} <span className="font-normal text-sm text-slate-500">· {a.email} · {fmt(a.created_at)}</span></p>
          <p className="text-sm">Livello dal test: <b>{a.level || 'test non svolto'}</b></p>
          {a.availability && <p className="text-sm text-slate-600">Disponibilità: {a.availability}</p>}
          {a.message && <p className="text-sm text-slate-600">{a.message}</p>}
        </div>
      ))}
    </div>
  );
};
